import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/id';
import { FiArrowRight, FiBriefcase, FiCheckCircle, FiClock, FiMapPin, FiMonitor, FiSunrise } from 'react-icons/fi';
import { PiGraduationCap } from 'react-icons/pi';
import { formatSalaryRange } from '../utils/formatSalary';
import { getJobCityName, JobCitySource } from '../utils/jobCity';

dayjs.extend(relativeTime);
dayjs.locale('id');

interface Level { id?: number; name?: string | null; }
type OptionalLevel = Level | string | null | undefined;
interface Position { position_loker_id: number; position_loker_name: string; }
type MetaItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  className: string;
};

interface Job extends JobCitySource {
  id: number;
  title: string;
  type: string;
  salary_min: number;
  salary_max: number;
  show_salary?: boolean;
  posted_date: string;
  experience?: OptionalLevel;
  experience_level?: OptionalLevel;
  education?: OptionalLevel;
  education_type?: OptionalLevel;
  education_level?: OptionalLevel;
  skills: Level[];
  positions?: Position[];
}

const getOptionalLevelName = (...values: OptionalLevel[]) => {
  for (const value of values) {
    const name = typeof value === 'string' ? value.trim() : value?.name?.trim();
    if (name) return name;
  }

  return null;
};

const typeColor = (type: string) => {
  switch (type) {
    case 'Penuh Waktu':
      return 'teal';
    case 'Hybrid':
      return 'amber';
    case 'Part Time':
      return 'slate';
    default:
      return 'teal';
  }
};

const typeIcon = (type: string) => {
  switch (type) {
    case 'Hybrid':
      return <FiMonitor />;
    case 'Part Time':
      return <FiSunrise />;
    default:
      return <FiBriefcase />;
  }
};

const JobCard: React.FC<{ job: Job; isApplied?: boolean }> = ({
  job,
  isApplied = false,
}) => {
  const navigate = useNavigate();
  const shouldShowSalary = job.show_salary !== false;
  const salary = shouldShowSalary ? formatSalaryRange(job.salary_min, job.salary_max) : null;
  const posted = dayjs(job.posted_date).fromNow();
  const experienceName = getOptionalLevelName(job.experience_level, job.experience);
  const educationName = getOptionalLevelName(job.education_level, job.education_type, job.education);
  const employmentType = typeof job.type === 'string' ? job.type.trim() : '';
  const cityName = getJobCityName(job);
  const isCompact = !experienceName && !educationName;
  const metaItems: MetaItem[] = [];

  if (employmentType) {
    metaItems.push({
      key: 'employment-type',
      label: employmentType,
      icon: typeIcon(employmentType),
      className: `job-card__type-badge job-card__type-badge--${typeColor(employmentType)}`,
    });
  }

  if (experienceName) {
    metaItems.push({
      key: 'experience',
      label: experienceName,
      icon: <FiClock />,
      className: 'job-card__meta-item',
    });
  }

  if (educationName) {
    metaItems.push({
      key: 'education',
      label: educationName,
      icon: <PiGraduationCap />,
      className: 'job-card__meta-item',
    });
  }

  const goToDetail = () => navigate(`/lowongan-kerja/${job.id}`);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') goToDetail();
  };

  return (
    <article className={`job-card${isCompact ? ' job-card--compact' : ''}`}>
      <div
        className="job-card__content"
        role="button"
        tabIndex={0}
        onClick={goToDetail}
        onKeyDown={onKey}
      >
        <div className="job-card__header">
          <h3 className="job-card__title">{job.title}</h3>
          {isApplied && (
            <div className="job-card__applied-badge">
              <FiCheckCircle />
              <span>Sudah Melamar</span>
            </div>
          )}
        </div>

        {cityName && (
          <p className="job-card__location">
            <FiMapPin />
            <span>{cityName}</span>
          </p>
        )}

        {shouldShowSalary && (
          <p className="job-card__salary">{salary}</p>
        )}

        {metaItems.length > 0 && (
          <div className="job-card__meta">
            {metaItems.map((item) => (
              <span key={item.key} className={item.className}>
                {item.icon}
                <span>{item.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="job-card__footer">
        <p className="job-card__posted">
          <FiClock />
          <span>Diposting {posted}</span>
        </p>
        <button type="button" className="job-card__detail-link" onClick={goToDetail}>
          <span>Lihat detail</span>
          <FiArrowRight />
        </button>
      </div>
    </article>
  );
};

export default JobCard;
